import { type CSSProperties, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Heart, Zap } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { WorldcooLogoIcon } from '@worldcoo/ui/custom/worldcoo-logo-icon';
import { Image } from '@/routes/-components/image';
import { LottiePlayer } from '../lottie/lottie-player';
import heroHandsAnimation from '../lottie/hero-home.json';
import { FLUID_FIGURE } from './stat-figure';

/**
 * The bento's tiles. Split out of `stats.tsx` for the 300-line rule; they are components rather than
 * inline JSX because the mobile and desktop layouts are different arrangements of the SAME tiles.
 */
export type TileProps = { className?: string; style?: CSSProperties };

/**
 * `valueClassName` is how the two figures shown IN FULL take `FLUID_FIGURE`; the short counters keep
 * the bigger flat size, because four digits fit the card at it and eleven do not.
 *
 * `value` is a node, not a string: the two live figures pass a `RollingFigure`, a row of rolling digit
 * columns, while the static tiles still pass a formatted string. `tabular-nums` below is what keeps an
 * animating figure from resizing its tile digit by digit.
 */
export function StatValue(props: { value: ReactNode; label: string; valueClassName?: string }) {
	return (
		<>
			{/* Hand-off sizes, from `md` only: below it these are a 17rem x 11rem swipe row it never draws. */}
			<p className={cn('text-4xl font-semibold tabular-nums md:text-[3.5rem]/[1.05]', props.valueClassName)}>
				{props.value}
			</p>
			<p className="mt-2 text-sm font-medium md:text-2xl">{props.label}</p>
		</>
	);
}

/**
 * What every tile takes: its footprint, and the `style` `Expand` merges onto it.
 *
 * `style` is not decoration. `Expand` with `asChild` hands its child a class AND an inline custom
 * property (the box's place in the arrival sweep), and a component child receives those as PROPS — so a
 * tile that forwards `className` but drops `style` joins the sweep with no place in the queue and every
 * box arrives at once.
 */

export function FundedProjectsTile(props: TileProps & { value: string }) {
	const { t } = useLingui();
	return (
		<div
			style={props.style}
			className={cn('bg-apricot text-ink flex flex-col rounded-3xl p-6 md:p-8', props.className)}
		>
			<Zap className="text-brand-strong size-6 fill-current" />
			<div className="mt-auto pt-6">
				<StatValue value={props.value} label={t`Funded projects`} />
			</div>
		</div>
	);
}

export function CoinsTile(props: TileProps) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('bg-sand relative overflow-hidden rounded-3xl', props.className)}>
			{/* The illustration is taken OUT OF FLOW, which is what keeps the tile from resizing under the
			    page. Laid out in flow it contributed nothing until `lottie-web` had loaded and then the
			    artboard's own height, growing this tile from 176px to 272px and the whole bento by 94px —
			    a layout shift a visitor sees a beat after the hero has painted. Out of flow the tile's
			    height is the grid's business alone, before and after, and the drawing simply fills it.

			    `cover` rather than `contain`: the tile is wider than the 420x327 artboard, so fitting the
			    whole thing inside left a gutter down each side and the drawing reading small. It fills the
			    tile edge to edge instead — the comms team asked for it bigger — and the crop lands at the
			    bottom of the arms, the part the artboard was already cutting off.

			    It plays ONCE. Left looping, the hands drop back out of frame and the tile spends a good
			    part of every three seconds empty, next to four figures that do not move. */}
			<LottiePlayer
				animationData={heroHandsAnimation}
				ariaLabel={t`Hands catching euro coins`}
				loop={false}
				fit="cover"
				className="absolute inset-0 h-full w-full"
			/>
		</div>
	);
}

export function TotalRaisedTile(props: TileProps & { value: ReactNode }) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('relative overflow-hidden rounded-3xl', props.className)}>
			{/* `height`/`aspectRatio` are the wide-screen tile's own box: `h-136` (544px) in one of three
			    columns of the 1200px bento, (1200 - 2 x 16) / 3 = 389px. They are what the srcset and `sizes`
			    are derived from, so a smaller box is a blurred photo: the old 360x480 made a DPR 1 screen
			    fetch the 360w candidate and stretch it 1.13x over the tile — sharp in dev only because dev
			    has no imgproxy and serves the original. The mobile row's 17rem x 11rem tile centre-crops it. */}
			<Image
				src="/redesign/hero-hands.jpg"
				alt={t`Two hands holding each other`}
				height={544}
				aspectRatio={390 / 544}
				objectFit="cover"
				className="absolute inset-0 h-full w-full object-cover!"
			/>
			{/* The container is this padded box, so `100cqi` is the room the figure actually has rather
			    than the tile's full width. */}
			<div className="@container absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white md:p-8">
				<StatValue value={props.value} label={t`Total raised`} valueClassName={FLUID_FIGURE} />
			</div>
		</div>
	);
}

export function MicrodonationsTile(props: TileProps & { value: ReactNode }) {
	const { t } = useLingui();
	return (
		<div
			style={props.style}
			className={cn(
				// `@container` here rather than on the inner box: this element carries the padding, so its
				// content box is the figure's real allowance.
				'@container bg-brand text-ink relative flex flex-col justify-end overflow-hidden rounded-3xl p-6 md:p-8',
				props.className,
			)}
		>
			{/* The donors app's watermark, in percentages rather than its fixed pixels: this tile is
			    portrait in the grid and landscape in the mobile row. */}
			<WorldcooLogoIcon
				aria-hidden
				className="pointer-events-none absolute -top-[26%] -left-[8%] h-auto w-[62%] text-white opacity-10 md:-top-[35%] md:-left-[19%] md:w-full"
			/>
			{/* An absolutely positioned sibling paints over in-flow content, so this needs its own order. */}
			<div className="relative z-10">
				<StatValue value={props.value} label={t`Microdonations received`} valueClassName={FLUID_FIGURE} />
			</div>
		</div>
	);
}

export function PartnerNgosTile(props: TileProps & { value: string }) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('bg-ink flex flex-col rounded-3xl p-6 text-white md:p-8', props.className)}>
			<Heart className="text-olive size-6 fill-current" />
			<div className="mt-auto pt-6">
				{/* `StatValue`'s sizes, repeated: this figure is olive on ink and has no seam there. */}
				<p className="text-olive text-4xl font-semibold tabular-nums md:text-[3.5rem]/[1.05]">{props.value}</p>
				<p className="mt-2 text-sm font-medium text-white/80 md:text-2xl">{t`Partner NGOs`}</p>
			</div>
		</div>
	);
}

/**
 * A photo tile. `height`/`aspectRatio` are the wide-screen side photo's own 220x384 box: the imgproxy
 * URL is derived from them, so every tile requests the very same file instead of a crop each. Sized
 * for the LARGER consumer on purpose — the mobile row's landscape tile centre-crops this portrait
 * happily, whereas the reverse would upscale.
 */
export function PhotoTile(props: TileProps & { src: string; alt: string }) {
	return (
		<div style={props.style} className={cn('overflow-hidden rounded-3xl', props.className)}>
			<Image
				src={props.src}
				alt={props.alt}
				height={384}
				aspectRatio={220 / 384}
				objectFit="cover"
				className="h-full w-full object-cover!"
			/>
		</div>
	);
}
